import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import { Element } from 'react-scroll';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

import GameDetails from 'Components/Bundle/BundleCarousels/GameDetails';
import GameSlickCarousel from 'Components/Bundle/BundleCarousels/GameSlickCarousel';
import PublicationDetails from 'Components/Bundle/BundleCarousels/PublicationDetails';
import PublicationSlickCarousel from 'Components/Bundle/BundleCarousels/PublicationSlickCarousel';
import BundleCarouselButtons from 'Components/Bundle/BundleCarousels/BundleCarouselButtons';

import 'Components/Bundle/BundleCarousels/BundleCarousel.scss';

const BundleCarousel = ({
  totalGameCount,
  product,
  allGames,
  hideTitle,
  slugsToBlur,
  hasRevealedAGift,
  lightThemeOverride,
  selectedSlide, // passed from the parent component anchors down to the carousel
}) => {
  const [showAll, setShowAll] = useState(false);
  const [slideNumber, setSlideNumber] = useState(1);
  const siteTheme = useSelector(state => state.siteTheme);
  const lightTheme = lightThemeOverride || siteTheme === 'light';
  const [resetScreenshots, setResetScreenshots] = useState(false);

  useEffect(() => {
    if (selectedSlide) {
      setSlideNumber(selectedSlide);
    }
  }, [selectedSlide]);

  useEffect(() => {
    if (selectedSlide !== slideNumber) {
      setResetScreenshots(true);
    }
  }, [slideNumber, selectedSlide]);

  const currentProduct = allGames.find(
    game => game.totalGameIndex === slideNumber,
  );

  if (!currentProduct) return false;

  const {
    age: ageRatings,
    cover,
    img: screenshots,
    display_type: displayType,
    video: videos,
    slug,
    video: youTubeVideos,
    carousel_type: carouselType,
  } = currentProduct;
  const productVideos = (videos && videos.map(video => ({ src: video, cover }))) || [];
  const isNonGame = ['book', 'comic', 'software', 'audio', 'video', 'elearning'].includes(displayType);
  const isGameCarousel = carouselType === 'game' || !isNonGame;
  const isPublicationCarousel = (carouselType === 'non game' || carouselType === 'non game landscape' || isNonGame) && carouselType !== 'game';

  let slidesCount = 0;
  if (cover && isEmpty(productVideos)) slidesCount = 1;
  if (screenshots) slidesCount += productVideos.length + screenshots.length;

  const DetailsComponent = isNonGame ? PublicationDetails : GameDetails;
  const SlickComponent = isPublicationCarousel ? PublicationSlickCarousel : GameSlickCarousel;

  const allSlugs = allGames.map(game => game.slug);
  const multiPackBundle = uniq(allSlugs).length === 1;
  const singleSlideCarousel = slidesCount === 1 && isGameCarousel;

  return (
    <section className={classNames('bundle-carousel', `${displayType}`, { 'carousel-light-theme': lightTheme, 'show-all': showAll })}>
      {!hasRevealedAGift && !isEmpty(slugsToBlur) &&
        <div className="blur-message">
          <FormattedMessage id="bundle-carousel.dontruinthesurprise" defaultMessage="Don't spoil the surprise! Click the gifts above to reveal them" />
        </div>
      }
      <Element name="carousel" className="element" >
        {!hideTitle &&
          <div className="carousel-title-container">
            <h2 className="section-title">
              {product.display_type && product.display_type.includes('bundle') &&
                <FormattedMessage id="bundle-carousel.bundle-details" defaultMessage="Bundle Details" />
              }
              {product.display_type && !product.display_type.includes('bundle') && product.template_type !== 'original' &&
                <FormattedMessage id="bundle-carousel.product-details" defaultMessage="Product Details" />
              }
              {product.display_type && !product.display_type.includes('bundle') && product.template_type === 'original' &&
                <FormattedMessage
                  id="bundle-carousel.game-name-details"
                  defaultMessage="Included in {name}"
                  values={{
                    name: product.name,
                  }}
                />
              }
            </h2>
            {totalGameCount > 1 && !multiPackBundle &&
              <BundleCarouselButtons
                totalGameCount={totalGameCount}
                slideNumber={slideNumber}
                setSlideNumber={setSlideNumber}
              />
            }
          </div>
        }

        <div
          className={classNames(
            'screenshot-carousel',
            { 'game-carousel': isGameCarousel },
            { 'single-slide-carousel': singleSlideCarousel },
            { 'publication-carousel': isPublicationCarousel },
            { 'publication-carousel--landscape': carouselType === 'non game landscape' },
          )}
        >
          <div className={classNames('video-container', { 'blur-content': slugsToBlur.includes(slug) })}>
            <SlickComponent
              bundleCover={cover}
              screenshots={screenshots}
              videos={productVideos}
              ratings={ageRatings}
              type="bundle"
              displayType={displayType}
              singleSlideCarousel={singleSlideCarousel}
              comicCover={cover}
              youTubeVideos={youTubeVideos}
              carouselType={carouselType}
              resetScreenshots={resetScreenshots}
              setResetScreenshots={setResetScreenshots}
            />
          </div>

          <DetailsComponent
            product={currentProduct}
            bundleSlug={product.slug}
            slugsToBlur={slugsToBlur}
            lightThemeOverride={lightThemeOverride}
          />
        </div>

        {hideTitle &&
          <div className="carousel-title-container carousel-buttons-below">
            {totalGameCount > 1 &&
              <BundleCarouselButtons
                totalGameCount={totalGameCount}
                slideNumber={slideNumber}
                setSlideNumber={setSlideNumber}
              />
            }
          </div>
        }
      </Element>

      {!showAll &&
        <div className="show-all-button-container">
          <button className="btn-reset-style" onClick={() => setShowAll(true)}>
            <FormattedMessage id="bundle-carousel.button-show-more" defaultMessage="Show more" />
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
        </div>
      }
    </section>
  );
};

BundleCarousel.propTypes = {
  totalGameCount: PropTypes.number,
  product: PropTypes.object,
  allGames: PropTypes.array,
  hideTitle: PropTypes.bool,
  slugsToBlur: PropTypes.array,
  hasRevealedAGift: PropTypes.bool,
  selectedSlide: PropTypes.number,
  lightThemeOverride: PropTypes.bool,
};

BundleCarousel.defaultProps = {
  totalGameCount: 0,
  product: {},
  allGames: [],
  hideTitle: false,
  slugsToBlur: [],
  hasRevealedAGift: false,
  selectedSlide: 1,
  lightThemeOverride: false,
};

export default BundleCarousel;
